/**
 * Browser launcher logic — what an idle browser panel should offer to open.
 *
 * Forge's idle panel used to be a centered globe and the sentence "Enter a URL
 * to load a page." True, and useless: the one thing the user almost always
 * wants is the app running in this thread, and the panel already knows its URL.
 *
 * t3code's preview panel (`apps/web/src/components/preview/PreviewEmptyState.tsx`)
 * makes the empty state a launcher instead — discovered local servers and
 * recently-visited URLs as rows you click. This module is that idea against
 * Forge's data. The inputs differ because the substrates differ: t3 scans the
 * developer's own machine for listening ports, while a Forge thread's app runs
 * inside a Sprite, so the equivalent signals are the thread's dev runtime and
 * the preview URL observed in its terminal output.
 *
 * Pure by design: every input is passed in, so the whole matrix is testable
 * without a browser, a session, or a runtime.
 *
 * @module browserLauncherLogic
 */
/** Where a launcher row came from. Drives its glyph and its section. */
export type BrowserLauncherTargetKind = "dev-server" | "recent";

export interface BrowserLauncherTarget {
  readonly kind: BrowserLauncherTargetKind;
  /** Absolute URL to navigate to. */
  readonly url: string;
  /** Page title when known, else null — the row falls back to `label`. */
  readonly title: string | null;
  /** `host/path` form, always shown: it is the part the user recognises. */
  readonly label: string;
  /** Secondary line: why it is live, or how long ago it was visited. */
  readonly detail: string | null;
  /** Epoch ms of the last visit, for recents only. */
  readonly lastVisitedAt: number | null;
}

export interface BrowserLauncherSections {
  /** Live things to open now: this thread's running or allocated app. */
  readonly live: ReadonlyArray<BrowserLauncherTarget>;
  /** Previously visited URLs for this project, newest first. */
  readonly recents: ReadonlyArray<BrowserLauncherTarget>;
  /**
   * Whether either section has a row. When false the launcher shows the small
   * empty state instead of two empty headings.
   */
  readonly hasTargets: boolean;
}

export interface BrowserLauncherRecentInput {
  readonly url: string;
  readonly title?: string | undefined;
  readonly lastVisitedAt: number;
}

export interface BrowserLauncherSectionsInput {
  /**
   * Preview URL observed in this thread's terminal output — the app the user
   * most likely wants. Already resolved by the panel; passed in rather than
   * recomputed.
   */
  readonly devServerPreviewUrl: string | null;
  /** Preview URL recorded on the thread's dev runtime row, when allocated. */
  readonly devRuntimePreviewUrl: string | null;
  readonly recents: ReadonlyArray<BrowserLauncherRecentInput>;
  /** Cap on recent rows. The rest stay in history but off this surface. */
  readonly recentLimit?: number | undefined;
}

const DEFAULT_RECENT_LIMIT = 8;

/**
 * Whether a URL is a signed dev-tunnel URL, which must never be shown or stored.
 *
 * The token in `?forgeTunnelToken=` is a credential, and the user-facing form of
 * a tunnelled preview is its loopback URL (`browserDisplayUrl`). Declared here
 * because both gates need it: the history store rejects it on write, and this
 * module drops it on read so a row persisted before that guard existed still
 * never reaches the screen.
 */
export function isDevTunnelPathUrl(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith("/api/dev-tunnel/");
  } catch {
    return false;
  }
}

/**
 * `host/path` for a URL, or null when it will not parse.
 *
 * Trailing `/` is dropped because `localhost:5173/` and `localhost:5173` are
 * the same place to a reader, and the slash is pure noise in a dense row.
 */
export function browserLauncherUrlLabel(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (isDevTunnelPathUrl(url)) {
    return null;
  }
  const path = parsed.pathname === "/" ? "" : parsed.pathname;
  return `${parsed.host}${path}${parsed.search}${parsed.hash}`;
}

/**
 * Identity for de-duplication: origin + path, ignoring query, hash and
 * trailing slash.
 *
 * Deliberately coarser than the URL. A dev server observed as
 * `http://localhost:5173/` and a recent visit to `http://localhost:5173` are
 * one place, and showing both would make the launcher look padded.
 */
function launcherDedupeKey(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const path = parsed.pathname.endsWith("/") ? parsed.pathname.slice(0, -1) : parsed.pathname;
  return `${parsed.protocol}//${parsed.host}${path}`;
}

function devServerTarget(input: {
  readonly url: string;
  readonly detail: string | null;
}): BrowserLauncherTarget | null {
  const label = browserLauncherUrlLabel(input.url);
  if (label === null) {
    return null;
  }
  return {
    kind: "dev-server",
    url: input.url,
    title: null,
    label,
    detail: input.detail,
    lastVisitedAt: null,
  };
}

function recentTarget(entry: BrowserLauncherRecentInput): BrowserLauncherTarget | null {
  const label = browserLauncherUrlLabel(entry.url);
  if (label === null) {
    return null;
  }
  return {
    kind: "recent",
    url: entry.url,
    title: entry.title ?? null,
    label,
    detail: null,
    lastVisitedAt: entry.lastVisitedAt,
  };
}

/**
 * The rows an idle browser panel should show.
 *
 * Ordering is by usefulness, not by source: the running dev server first (it is
 * why the panel was opened), then history. Anything already offered as live is
 * dropped from recents rather than repeated — a launcher that lists the same URL
 * twice reads as a bug.
 *
 * Dev tunnels are deliberately absent. A tunnel is not a separate destination —
 * it is the same app behind a signed URL, and opening the loopback URL already
 * provisions one through `ensureDevTunnelPreviewUrl`. Listing tunnels would both
 * duplicate the dev-server row and print a signed `forgeTunnelToken` on screen,
 * which is exactly what `browserDisplayUrl` exists to prevent.
 */
export function resolveBrowserLauncherSections(
  input: BrowserLauncherSectionsInput,
): BrowserLauncherSections {
  const seen = new Set<string>();
  const live: BrowserLauncherTarget[] = [];

  const pushLive = (target: BrowserLauncherTarget | null): void => {
    if (target === null) {
      return;
    }
    const key = launcherDedupeKey(target.url);
    if (key === null || seen.has(key)) {
      return;
    }
    seen.add(key);
    live.push(target);
  };

  pushLive(
    input.devServerPreviewUrl === null
      ? null
      : devServerTarget({ url: input.devServerPreviewUrl, detail: "Running in this thread" }),
  );
  pushLive(
    input.devRuntimePreviewUrl === null
      ? null
      : devServerTarget({ url: input.devRuntimePreviewUrl, detail: "Allocated for this thread" }),
  );

  const recents: BrowserLauncherTarget[] = [];
  const limit = input.recentLimit ?? DEFAULT_RECENT_LIMIT;
  for (const entry of input.recents) {
    if (recents.length >= limit) {
      break;
    }
    const target = recentTarget(entry);
    if (target === null) {
      continue;
    }
    const key = launcherDedupeKey(target.url);
    if (key === null || seen.has(key)) {
      continue;
    }
    seen.add(key);
    recents.push(target);
  }

  return { live, recents, hasTargets: live.length > 0 || recents.length > 0 };
}

/**
 * Whether the panel's status pane should be the launcher rather than a message.
 *
 * Only the two idle statuses qualify. `connecting`, `failed`,
 * `navigation-pending` and `navigation-failed` each name something that just
 * happened, and replacing that with a list of links would hide it — the exact
 * silent-failure the status surface exists to prevent.
 */
export function shouldShowBrowserLauncher(status: string): boolean {
  return status === "no-session" || status === "idle";
}
